import type {MessageApi} from 'naive-ui';
import {isStaleWorkerResponseError} from '@puppycad/cad-core';

/**
 * 编辑器异步错误上报输入。
 */
export interface EditorAsyncFailureReportInput {
  /** 原始错误对象。 */
  readonly error: unknown;
  /** 原始错误不可读时使用的兜底提示。 */
  readonly fallbackMessage: string;
  /** UI 消息 API。 */
  readonly message: Pick<MessageApi, 'error'>;
  /** 浏览器全局错误上报入口。 */
  readonly reportError?: (error: Error) => void;
}

/**
 * 判断编辑器异步错误是否应该静默忽略。
 *
 * @param error 原始错误对象。
 * @returns 需要静默忽略时返回 true。
 */
export function shouldSuppressEditorAsyncFailure(error: unknown): boolean {
  /**
   * Worker stale 是 undo/redo、退出草图、重建 epoch 推进后的预期取消流。
   * 它不能进入 `window.reportError`，否则浏览器会把已捕获的取消误报成 Uncaught。
   */
  return isStaleWorkerResponseError(error);
}

/**
 * 统一上报编辑器异步错误。
 *
 * @param input 错误上报输入。
 */
export function reportEditorAsyncFailure(input: EditorAsyncFailureReportInput): void {
  if (shouldSuppressEditorAsyncFailure(input.error)) {
    return;
  }

  const normalizedError = input.error instanceof Error
    ? input.error
    : new Error(input.fallbackMessage);

  input.message.error(normalizedError.message || input.fallbackMessage);
  input.reportError?.(normalizedError);
}
